import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { User, Membership, RoleCategory } from "@/lib/types";

interface AuthState {
  user: User | null;
  loading: boolean;
  activeMembership: Membership | null;
  modules: Record<string, boolean>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
  hasPerm: (perm: string) => boolean;
  roleCategory: RoleCategory;
}

const AuthCtx = createContext<AuthState | undefined>(undefined);

const LEADERSHIP_ROLES = ["tenant_admin", "corporate_exec", "regional_manager", "district_manager", "auditor"];
const STORE_ROLES = ["store_manager", "assistant_manager"];

function computeCategory(m: Membership | null): RoleCategory {
  if (!m) return "frontline";
  if (m.roles.some((r) => LEADERSHIP_ROLES.includes(r))) return "leadership";
  if (m.roles.some((r) => STORE_ROLES.includes(r))) return "store";
  return "frontline";
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<Record<string, boolean>>({});

  const activeMembership = user?.memberships?.[0] ?? null;

  const loadModules = useCallback(async (m: Membership | null) => {
    if (!m) {
      setModules({});
      return;
    }
    // Enabled modules are delivered with the membership payload for every
    // authenticated user (not gated behind org.view), so the UI can gate
    // inactive modules for store/frontline roles too.
    if (m.modules) {
      setModules(m.modules);
      return;
    }
    if (!m.permissions.includes("org.view")) {
      setModules({});
      return;
    }
    try {
      const res = await api.get("/admin/modules");
      setModules(res.data.modules || {});
    } catch {
      setModules({});
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data.user);
      await loadModules(res.data.user?.memberships?.[0] ?? null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadModules]);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data.user);
    await loadModules(res.data.user?.memberships?.[0] ?? null);
  }, [loadModules]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      try {
        const { clearAll } = await import("@/pwa/offlineQueue");
        const { clearApiCache } = await import("@/pwa/registerSW");
        clearAll();          // never carry queued mutations across a logout / user switch
        clearApiCache();     // drop cached tenant read data on logout
      } catch { /* pwa optional */ }
      setUser(null);
      setModules({});
    }
  }, []);

  const hasPerm = useCallback(
    (perm: string) => !!activeMembership?.permissions.includes(perm),
    [activeMembership]
  );

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        activeMembership,
        modules,
        login,
        logout,
        refreshMe,
        hasPerm,
        roleCategory: computeCategory(activeMembership),
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
